import {
  CAMPAIGN_APPROVAL_FIELD_CREATIVE_LABEL,
  CAMPAIGN_APPROVAL_FIELD_DESCRIPTION_LABEL,
  CAMPAIGN_APPROVAL_FIELD_HEADLINE_LABEL,
  CAMPAIGN_APPROVAL_FIELD_LONG_HEADLINE_LABEL,
  CAMPAIGN_APPROVAL_FIELD_TITLE_LABEL,
  CAMPAIGN_APPROVAL_SLIDE_LABEL,
  CAMPAIGN_APPROVAL_STATUS_APPROVED,
  CAMPAIGN_APPROVAL_STATUS_PENDING,
  CAMPAIGN_APPROVAL_STATUS_REJECTED,
  CAMPAIGN_APPROVAL_TARGET_ENTITY_AD,
  CAMPAIGN_APPROVAL_TARGET_ENTITY_ASSETS_GROUP,
  CREATIVE,
} from "../../i18n/keysTranslations";
import { LINKEDIN, META, TIKTOK, BORDER_RADIUS } from "../../utils/constants";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";

export const CAMPAIGN_APPROVAL_CONTENT_MAX_WIDTH = 900;

export const campaignApprovalMainContentSx = {
  width: "100%",
  maxWidth: CAMPAIGN_APPROVAL_CONTENT_MAX_WIDTH,
  mx: "auto",
};

export const campaignApprovalMainPaddingSx = {
  px: { xs: 2, md: 3 },
};

export const campaignApprovalNoticeAlertSx = {
  backgroundColor: "fields.background",
  color: "background.paperContrastText",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: BORDER_RADIUS,
  boxShadow: BOX_SHADOW_FIELDS,
};

const CAROUSEL_APPROVAL_PLATFORMS = new Set([META, TIKTOK, LINKEDIN]);

export const getCampaignApprovalTargetKey = (target = {}) => {
  return [
    target?.platform,
    target?.entityType,
    target?.entityID,
    target?.fieldType,
    target?.fieldIndex,
    target?.mediaIndex,
  ]
    .filter((item) => item !== undefined && item !== null && item !== "")
    .join(":");
};

export const getCampaignApprovalStatusLabel = (status, t) => {
  if (status === "approved") {
    return t(CAMPAIGN_APPROVAL_STATUS_APPROVED);
  }

  if (status === "rejected") {
    return t(CAMPAIGN_APPROVAL_STATUS_REJECTED);
  }

  return t(CAMPAIGN_APPROVAL_STATUS_PENDING);
};

export const getCampaignApprovalStatusSx = (status) => {
  if (status === "approved") {
    return {
      bgcolor: "rgba(19, 115, 51, 0.1)",
      borderColor: "rgba(19, 115, 51, 0.3)",
      color: "#137333",
    };
  }

  if (status === "rejected") {
    return {
      bgcolor: "rgba(197, 34, 31, 0.1)",
      borderColor: "rgba(197, 34, 31, 0.3)",
      color: "#c5221f",
    };
  }

  return {
    bgcolor: "rgba(255, 193, 7, 0.12)",
    borderColor: "rgba(255, 193, 7, 0.35)",
    color: "#8a6200",
  };
};

export const getCampaignApprovalFieldLabel = (
  fieldType,
  t,
  position = null
) => {
  const labelMap = {
    title: CAMPAIGN_APPROVAL_FIELD_TITLE_LABEL,
    description: CAMPAIGN_APPROVAL_FIELD_DESCRIPTION_LABEL,
    headline: CAMPAIGN_APPROVAL_FIELD_HEADLINE_LABEL,
    longHeadline: CAMPAIGN_APPROVAL_FIELD_LONG_HEADLINE_LABEL,
    creative: CAMPAIGN_APPROVAL_FIELD_CREATIVE_LABEL,
  };

  const baseLabel = t(labelMap[fieldType] || CREATIVE);
  if (!Number.isInteger(position)) {
    return baseLabel;
  }

  return `${baseLabel} ${position + 1}`;
};

export const isCarouselCreativesSection = (creatives = []) =>
  creatives.some((creative) => Boolean(creative?.isCarousel));

export const isCarouselApprovalPlatform = (platform) =>
  CAROUSEL_APPROVAL_PLATFORMS.has(platform);

export const getCreativeCommentTarget = (target = {}) => {
  if (!target?.entityID) {
    return null;
  }

  const fieldType = target.fieldType || "creative";

  if (fieldType !== "creative") {
    return {
      ...target,
      fieldType,
    };
  }

  return {
    ...target,
    fieldType: "creative",
    mediaIndex: Number.isInteger(target.mediaIndex) ? target.mediaIndex : 0,
  };
};

export const getCampaignApprovalCreativeItemLabel = ({
  creative,
  isCarouselSection,
  isSingularCreativeSection,
  t,
}) => {
  if (isSingularCreativeSection || !isCarouselSection) {
    return null;
  }

  const slideIndex = Number.isInteger(creative?.mediaIndex)
    ? creative.mediaIndex + 1
    : 1;

  return `${t(CAMPAIGN_APPROVAL_SLIDE_LABEL)} ${slideIndex}`.toUpperCase();
};

export const getCampaignApprovalTargetSummary = (target, t) => {
  if (!target) {
    return "";
  }

  const entityLabel =
    target?.entityType === "assetsGroup"
      ? t(CAMPAIGN_APPROVAL_TARGET_ENTITY_ASSETS_GROUP)
      : t(CAMPAIGN_APPROVAL_TARGET_ENTITY_AD);

  const itemIndex =
    Number.isInteger(target?.fieldIndex) || Number.isInteger(target?.mediaIndex)
      ? (target?.fieldIndex ?? target?.mediaIndex)
      : null;

  const fieldLabel = getCampaignApprovalFieldLabel(
    target?.fieldType,
    t,
    itemIndex
  );

  return [t(target?.platform), entityLabel, fieldLabel]
    .filter(Boolean)
    .join(" · ");
};

export const getEntityAdGroupName = (entity = {}) => {
  if (entity?.adGroupName) {
    return entity.adGroupName;
  }

  const labelParts = String(entity?.label || "")
    .split(" / ")
    .map((part) => part.trim())
    .filter(Boolean);

  return labelParts[0] || entity?.label || "";
};

export const getEntityAdName = (entity = {}) => {
  if (entity?.adName) {
    return entity.adName;
  }

  const labelParts = String(entity?.label || "")
    .split(" / ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (labelParts.length > 1) {
    return labelParts.slice(1).join(" / ");
  }

  return entity?.entityType === "assetsGroup"
    ? entity?.label || ""
    : labelParts[0] || entity?.label || "";
};

export const isApprovalAssetsGroupEntity = (entity = {}) =>
  entity?.entityType === "assetsGroup";

export const groupEntitiesByAdGroup = (entities = []) => {
  return (entities || []).reduce((groups, entity) => {
    if (isApprovalAssetsGroupEntity(entity)) {
      return groups;
    }

    const adGroupName = getEntityAdGroupName(entity) || "default";
    const currentGroup = groups[adGroupName] || [];

    return {
      ...groups,
      [adGroupName]: [...currentGroup, entity],
    };
  }, {});
};

export const groupApprovalEntitiesForDisplay = (entities = []) => {
  const sections = [];
  let currentAdGroupSection = null;

  const flushAdGroupSection = () => {
    if (!currentAdGroupSection) {
      return;
    }

    sections.push(currentAdGroupSection);
    currentAdGroupSection = null;
  };

  for (const entity of entities || []) {
    if (isApprovalAssetsGroupEntity(entity)) {
      flushAdGroupSection();
      sections.push({
        type: "assetsGroup",
        key: `assetsGroup-${entity.entityID}`,
        entities: [entity],
      });
      continue;
    }

    const adGroupName = getEntityAdGroupName(entity) || "default";

    if (!currentAdGroupSection || currentAdGroupSection.title !== adGroupName) {
      flushAdGroupSection();
      currentAdGroupSection = {
        type: "adGroup",
        key: `adGroup-${adGroupName}`,
        title: adGroupName,
        entities: [entity],
      };
      continue;
    }

    currentAdGroupSection.entities.push(entity);
  }

  flushAdGroupSection();

  return sections;
};

export const countPlatformSummary = (entities = []) => {
  const adGroupNames = new Set();

  return (entities || []).reduce(
    (summary, entity) => {
      const adGroupName = getEntityAdGroupName(entity);
      if (adGroupName) {
        adGroupNames.add(adGroupName);
      }

      return {
        adSets: adGroupNames.size,
        ads: summary.ads + 1,
        texts: summary.texts + (entity?.texts?.length || 0),
        creatives: summary.creatives + (entity?.creatives?.length || 0),
      };
    },
    { adSets: 0, ads: 0, texts: 0, creatives: 0 }
  );
};

export const isCommentForCurrentDraft = (comment = {}, currentDraftID = "") => {
  if (!comment || comment?.isStale) {
    return false;
  }

  if (!currentDraftID) {
    return true;
  }

  return !comment?.draftID || comment?.draftID === currentDraftID;
};

export const getCommentsForTarget = (
  comments = [],
  target = {},
  currentDraftID = ""
) => {
  const targetKey = getCampaignApprovalTargetKey(target);

  return (comments || []).filter((comment) => {
    if (!isCommentForCurrentDraft(comment, currentDraftID)) {
      return false;
    }

    if (comment?.type !== "specific") {
      return false;
    }

    return getCampaignApprovalTargetKey(comment?.target) === targetKey;
  });
};

export const countEntityComments = (
  comments = [],
  entity = {},
  currentDraftID = ""
) => {
  const textTargets = (entity?.texts || []).map(
    (textEntry) => textEntry.target
  );
  const creativeTargets = (entity?.creatives || [])
    .map((creative) => getCreativeCommentTarget(creative.target))
    .filter(Boolean);

  return [...textTargets, ...creativeTargets].reduce((total, target) => {
    return (
      total + getCommentsForTarget(comments, target, currentDraftID).length
    );
  }, 0);
};

export const countReviewerComments = (comments = [], currentDraftID = "") => {
  return (comments || []).filter(
    (comment) =>
      isCommentForCurrentDraft(comment, currentDraftID) &&
      (comment?.type === "general" || comment?.type === "specific")
  ).length;
};

export const formatApprovalCountLabel = (count, pluralKey, singularKey, t) => {
  const normalizedCount = Number(count) || 0;
  return t(normalizedCount === 1 ? singularKey : pluralKey, {
    count: normalizedCount,
  });
};

const FIELD_TYPE_TO_FORM_KEY = {
  title: "titles",
  description: "descriptions",
  headline: "headlines",
  longHeadline: "longHeadlines",
};

const CREATIVE_FIELD_KEYS = ["mediaUrls", "imageUrls", "videoUrls", "logoUrls"];

const findAdGroupAndAdByEntityID = (adsGroups = [], entityID = "") => {
  for (let adGroupIndex = 0; adGroupIndex < adsGroups.length; adGroupIndex++) {
    const adGroup = adsGroups[adGroupIndex];
    const adIndex = (adGroup?.ads || []).findIndex((ad) => ad?.id === entityID);

    if (adIndex !== -1) {
      return {
        adGroupIndex,
        adIndex,
        adGroup,
        ad: adGroup.ads[adIndex],
      };
    }
  }

  return null;
};

const buildTextFieldPath = (basePath, fieldType, fieldIndex = 0) => {
  const formKey = FIELD_TYPE_TO_FORM_KEY[fieldType];

  if (!formKey) {
    return null;
  }

  return `${basePath}.${formKey}.[${fieldIndex}].value`;
};

const buildCreativeFieldPath = (basePath, mediaIndex = 0) => {
  return `${basePath}.mediaUrls.[${mediaIndex}]`;
};

export const getApprovalCommentEntityLines = ({ target, getValues } = {}) => {
  if (!target?.entityID || !getValues) {
    return [];
  }

  const values = getValues();

  if (target.entityType === "ad") {
    const match = findAdGroupAndAdByEntityID(
      values?.adsGroups || [],
      target.entityID
    );

    return [
      { type: "adGroup", name: match?.adGroup?.name || null },
      { type: "ad", name: match?.ad?.name || null },
    ];
  }

  if (target.entityType === "assetsGroup") {
    const assetsGroup = (values?.assetsGroups || []).find(
      (group) => group?.id === target.entityID
    );

    return [{ type: "assetsGroup", name: assetsGroup?.name || null }];
  }

  return [];
};

export const mapApprovalTargetToFieldPath = ({ target, getValues } = {}) => {
  if (!target?.entityType || !target?.entityID || !getValues) {
    return null;
  }

  const values = getValues();

  if (target.entityType === "ad") {
    const match = findAdGroupAndAdByEntityID(
      values?.adsGroups || [],
      target.entityID
    );

    if (!match) {
      return null;
    }

    const basePath = `adsGroups.[${match.adGroupIndex}].ads.[${match.adIndex}]`;

    if (target.fieldType === "creative") {
      const mediaIndex = target.mediaIndex ?? 0;
      const entity = match.ad;

      const creativeField = CREATIVE_FIELD_KEYS.find((fieldKey) => {
        const urls = entity?.[fieldKey];
        return Array.isArray(urls) && urls.length > mediaIndex;
      });

      if (creativeField) {
        return `${basePath}.${creativeField}.[${mediaIndex}]`;
      }

      return buildCreativeFieldPath(basePath, mediaIndex);
    }

    return buildTextFieldPath(
      basePath,
      target.fieldType,
      target.fieldIndex ?? 0
    );
  }

  if (target.entityType === "assetsGroup") {
    const assetsGroupIndex = (values?.assetsGroups || []).findIndex(
      (group) => group?.id === target.entityID
    );

    if (assetsGroupIndex === -1) {
      return null;
    }

    const basePath = `assetsGroups.[${assetsGroupIndex}]`;

    if (target.fieldType === "creative") {
      const mediaIndex = target.mediaIndex ?? 0;
      const assetsGroup = values.assetsGroups[assetsGroupIndex];

      const creativeField = CREATIVE_FIELD_KEYS.find((fieldKey) => {
        const urls = assetsGroup?.[fieldKey];
        return Array.isArray(urls) && urls.length > mediaIndex;
      });

      if (creativeField) {
        return `${basePath}.${creativeField}.[${mediaIndex}]`;
      }

      return buildCreativeFieldPath(basePath, mediaIndex);
    }

    return buildTextFieldPath(
      basePath,
      target.fieldType,
      target.fieldIndex ?? 0
    );
  }

  return null;
};

export const mapApprovalTargetToSection = ({ target, adsGroups = [] } = {}) => {
  if (!target?.platform || !target?.entityType || !target?.entityID) {
    return null;
  }

  if (target.entityType === "assetsGroup") {
    return {
      platform: target.platform,
      assetsGroupID: target.entityID,
      adGroupID: undefined,
      adID: undefined,
    };
  }

  if (target.entityType === "ad") {
    const matchedAdGroup = (adsGroups || []).find((adGroup) =>
      (adGroup?.ads || []).some((ad) => ad?.id === target.entityID)
    );

    if (!matchedAdGroup) {
      return null;
    }

    return {
      platform: target.platform,
      assetsGroupID: undefined,
      adGroupID: matchedAdGroup.id,
      adID: target.entityID,
    };
  }

  return null;
};
